import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Table, Button, Space, Input, message, Tooltip, Popconfirm, Tag, Modal, Select } from 'antd';
import {
  CrownOutlined,
  ReloadOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  UserSwitchOutlined,
} from '@ant-design/icons';
import MainLayout from '../../layouts/MainLayout';
import { superadminUsersApi, show422Error } from '../../utils/api';
import { PANEL_PRIMARY_BTN_CLASS } from '../../constants/ui';
import { TableSkeleton } from '../../components/SkeletonLoaders';
import { formatApiDateTimeJalali } from '../../utils/dateUtils';
import { useListReturnState } from '../../hooks/useListReturnState';

const { Option } = Select;

const pickPaginator = (res) => {
  const body = res || {};
  return body.data && Array.isArray(body.data.data) ? body.data : body;
};

const SuperadminUsersSettings = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });

  const [assignOpen, setAssignOpen] = useState(false);
  const [assignUser, setAssignUser] = useState(null);
  const [roleOptions, setRoleOptions] = useState([]);
  const [assignRoleId, setAssignRoleId] = useState(undefined);
  const [assignLoading, setAssignLoading] = useState(false);
  const pageSizeRef = useRef(10);
  pageSizeRef.current = pagination.pageSize;

  const captureCurrentListState = useCallback(
    () => ({
      search,
      pagination: {
        current: pagination.current,
        pageSize: pagination.pageSize,
      },
    }),
    [search, pagination.current, pagination.pageSize],
  );

  const { navigateToDetail } = useListReturnState(captureCurrentListState, (restored) => {
    if (restored.search != null) setSearch(restored.search);
    if (restored.pagination) {
      setPagination((prev) => ({
        ...prev,
        current: restored.pagination.current || 1,
        pageSize: restored.pagination.pageSize || prev.pageSize,
      }));
    }
  });

  const fetchList = useCallback(
    async (page = 1, perPage = 10) => {
      setLoading(true);
      try {
        const res = await superadminUsersApi.list({
          search: search.trim() || undefined,
          page,
          per_page: perPage,
        });
        const pag = pickPaginator(res);
        setRows(pag.data || []);
        setPagination({
          current: Number(pag.current_page || page),
          pageSize: Number(pag.per_page || perPage),
          total: Number(pag.total || 0),
        });
      } catch (error) {
        message.error(error?.message || 'خطا در دریافت لیست');
      } finally {
        setLoading(false);
      }
    },
    [search],
  );

  useEffect(() => {
    const q = search.trim();
    const delay = q ? 350 : 0;
    const t = setTimeout(() => {
      fetchList(1, pageSizeRef.current);
    }, delay);
    return () => clearTimeout(t);
  }, [search, fetchList]);

  const onDelete = useCallback(
    async (id) => {
      try {
        await superadminUsersApi.delete(id);
        message.success('حذف شد');
        fetchList(pagination.current, pagination.pageSize);
      } catch (error) {
        message.error(error?.message || 'خطا در حذف');
      }
    },
    [fetchList, pagination.current, pagination.pageSize],
  );

  const openAssign = useCallback(async (record) => {
    setAssignUser(record);
    setAssignRoleId(record.roleId ?? record?.role?.[0]?.role_id);
    setAssignOpen(true);
    setAssignLoading(true);
    try {
      const res = await superadminUsersApi.getCreateMeta();
      const d = res?.data || res;
      setRoleOptions(d.roles || []);
    } catch (error) {
      message.error(error?.message || 'خطا در دریافت نقش‌ها');
      setRoleOptions([]);
    } finally {
      setAssignLoading(false);
    }
  }, []);

  const submitAssign = useCallback(async () => {
    if (!assignUser?.id || assignRoleId == null) return;
    setAssignLoading(true);
    try {
      await superadminUsersApi.assignRole({
        user_id: assignUser.id,
        role: assignRoleId,
      });
      message.success('نقش اختصاص داده شد');
      setAssignOpen(false);
      setAssignUser(null);
      fetchList(pagination.current, pagination.pageSize);
    } catch (error) {
      if (error?.name === 'ValidationError' || error?.details) show422Error(error);
      else message.error(error?.message || 'خطا در اختصاص نقش');
    } finally {
      setAssignLoading(false);
    }
  }, [assignUser, assignRoleId, fetchList, pagination.current, pagination.pageSize]);

  const statusTag = (s) => {
    const v = String(s || '').toLowerCase();
    if (v === 'active') return <Tag color="green">فعال</Tag>;
    if (v === 'deactive') return <Tag color="default">غیرفعال</Tag>;
    return <Tag>{s || '—'}</Tag>;
  };

  const columns = useMemo(
    () => [
      {
        title: 'کاربر',
        key: 'user',
        ellipsis: true,
        render: (_, r) => (
          <div className="flex items-center gap-3 min-w-0 py-0.5">
            {r.image_url ? (
              <span className="inline-flex flex-shrink-0 overflow-hidden rounded-full border border-gray-200 dark:border-gray-600">
                <img src={r.image_url} alt="" className="block h-9 w-9 object-cover sm:h-10 sm:w-10" />
              </span>
            ) : null}
            <div className="min-w-0 flex-1">
              <div className="truncate font-semibold text-gray-800 dark:text-gray-100" style={{ fontFamily: 'CustomFont' }}>
                {r.name || '—'}
              </div>
              <div className="truncate text-xs text-gray-500 dark:text-gray-400" dir="ltr">
                {r.email || '—'}
              </div>
            </div>
          </div>
        ),
      },
      {
        title: 'نقش',
        dataIndex: 'roleName',
        key: 'roleName',
        width: 160,
        ellipsis: true,
        render: (t) => <span style={{ fontFamily: 'CustomFont' }}>{t || '—'}</span>,
      },
      {
        title: 'وضعیت',
        dataIndex: 'status',
        key: 'status',
        width: 110,
        render: statusTag,
      },
      {
        title: 'تاریخ ایجاد',
        dataIndex: 'created_at',
        key: 'created_at',
        width: 170,
        responsive: ['md'],
        ellipsis: true,
        render: (t) => formatApiDateTimeJalali(t) || '—',
      },
      {
        title: 'عملیات',
        key: 'actions',
        width: 220,
        align: 'center',
        fixed: 'right',
        render: (_, r) => (
          <div className="flex w-full justify-center">
            <Space size="small">
              <Tooltip title="اختصاص نقش">
                <Button
                  type="text"
                  icon={<UserSwitchOutlined />}
                  onClick={() => openAssign(r)}
                  className="text-purple-600 hover:text-purple-700 hover:bg-purple-50 dark:hover:bg-purple-900/20"
                />
              </Tooltip>
              <Tooltip title="ویرایش">
                <Button
                  type="text"
                  icon={<EditOutlined />}
                  onClick={() => navigateToDetail(`/settings/superadmin-users/${r.id}/edit`)}
                  className="text-green-600 hover:text-green-700 hover:bg-green-50 dark:hover:bg-green-900/20"
                />
              </Tooltip>
              <Popconfirm title="حذف این کاربر؟" okText="حذف" cancelText="لغو" onConfirm={() => onDelete(r.id)}>
                <Tooltip title="حذف">
                  <Button type="text" danger icon={<DeleteOutlined />} />
                </Tooltip>
              </Popconfirm>
            </Space>
          </div>
        ),
      },
    ],
    [navigateToDetail, onDelete, openAssign],
  );

  return (
    <MainLayout>
      <div className="custom-font w-full min-w-0">
        <Card className="shadow-xl border-0" styles={{ body: { padding: 0 } }}>
          <div className="flex flex-wrap items-center justify-between gap-4 border-b border-gray-200 bg-white p-4 sm:p-6 dark:border-gray-700 dark:bg-gray-800 rounded-t-xl">
            <div className="flex items-center gap-3 min-w-0">
              <div className="flex h-11 w-11 sm:h-12 sm:w-12 items-center justify-center rounded-xl bg-violet-50 dark:bg-violet-900/25 flex-shrink-0">
                <CrownOutlined className="text-lg sm:text-xl text-violet-600 dark:text-violet-400" />
              </div>
              <div className="min-w-0">
                <h1 className="m-0 text-xl sm:text-2xl font-bold text-gray-800 dark:text-gray-100 truncate">سوپر ادمین</h1>
                <p className="text-gray-500 dark:text-gray-400 text-xs sm:text-sm m-0 truncate">مدیریت کاربران با دسترسی سوپرادمین</p>
              </div>
            </div>
            <Space wrap>
              <Button icon={<ReloadOutlined />} loading={loading} onClick={() => fetchList(pagination.current, pagination.pageSize)}>
                بروزرسانی
              </Button>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                className={PANEL_PRIMARY_BTN_CLASS}
                onClick={() => navigate('/settings/superadmin-users/create')}
              >
                کاربر جدید
              </Button>
            </Space>
          </div>

          <div className="bg-gray-50 p-4 sm:p-6 dark:bg-gray-900/50 rounded-b-xl">
            <div className="mb-6 flex flex-wrap items-center gap-3">
              <Input
                allowClear
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="جستجو..."
                prefix={<SearchOutlined className="text-gray-400" />}
                className="rounded-lg"
                style={{ fontFamily: 'CustomFont', maxWidth: 360, width: '100%', minWidth: 220 }}
                size="middle"
              />
            </div>

            <Card className="border border-gray-100 dark:border-gray-700 dark:bg-gray-800 overflow-hidden">
              {loading ? (
                <div className="p-4 sm:p-5">
                  <TableSkeleton rows={8} columns={4} />
                </div>
              ) : (
                <Table
                  rowKey="id"
                  loading={false}
                  columns={columns}
                  dataSource={rows}
                  pagination={{
                    current: pagination.current,
                    pageSize: pagination.pageSize,
                    total: pagination.total,
                    showSizeChanger: true,
                    responsive: true,
                    onChange: (page, pageSize) => fetchList(page, pageSize),
                  }}
                  scroll={{ x: 'max-content' }}
                />
              )}
            </Card>
          </div>
        </Card>
      </div>

      <Modal
        wrapClassName="custom-font"
        classNames={{ body: 'custom-font' }}
        title={
          <div className="flex items-center gap-3 py-1">
            <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-violet-50 dark:bg-violet-900/25">
              <UserSwitchOutlined className="text-lg text-violet-600 dark:text-violet-400" />
            </span>
            <div className="min-w-0 text-right">
              <div className="text-base font-bold text-gray-800 dark:text-gray-100">اختصاص نقش</div>
              <div className="text-xs font-normal text-gray-500 dark:text-gray-400 mt-0.5">انتخاب نقش برای کاربر سوپرادمین</div>
            </div>
          </div>
        }
        open={assignOpen}
        onCancel={() => {
          setAssignOpen(false);
          setAssignUser(null);
        }}
        onOk={submitAssign}
        okText="ثبت"
        cancelText="لغو"
        confirmLoading={assignLoading}
        okButtonProps={{ className: PANEL_PRIMARY_BTN_CLASS }}
        cancelButtonProps={{ style: { fontFamily: 'CustomFont' } }}
        destroyOnClose
        width={480}
      >
        {assignUser ? (
          <div className="space-y-3" style={{ fontFamily: 'CustomFont' }}>
            <div className="text-sm text-gray-600 dark:text-gray-300">
              کاربر: <strong>{assignUser.name}</strong>
            </div>
            <div>
              <div className="text-xs text-gray-500 mb-1">نقش</div>
              <Select
                className="w-full"
                value={assignRoleId}
                onChange={setAssignRoleId}
                loading={assignLoading}
                popupClassName="custom-font"
                placeholder="انتخاب نقش"
              >
                {roleOptions.map((role) => (
                  <Option key={role.id} value={role.id}>
                    {role.display_name}
                  </Option>
                ))}
              </Select>
            </div>
          </div>
        ) : null}
      </Modal>
    </MainLayout>
  );
};

export default SuperadminUsersSettings;
